// NIGHT-DIRECTSTAFF-2026-10-22 §2: "Ending soon" -- placements on contract (started / ending / extended) whose end date is
// within 4 weeks, soonest first, each with the re-placement agent's status and a Text button that opens the thread.
//
// The agent's status (agents window, NIGHT-AGENTS-2026-10-22 §1; read only): agent_lead_decisions rows with decision
// 'replacement' and reason '<step>:<placement id>@<end_date YYYY-MM-DD>'. Keyed by the CURRENT end date, so an extension
// (a new end date) starts again at "not started". Steps -> status (machine value, then the words shown):
//   no row                                   -> not_started       Not started
//   opener                                   -> texted            Texted
//   nudge                                    -> texted_twice      Texted twice
//   ask_state | reply_next                   -> next_requested    Next requested
//   reply_extend                             -> extend_requested  Extend requested
//   reply_leaving                            -> leaving           Leaving (placement_outcome is set too)
//   opener + nudge, no reply step, more than 7 days after the nudge -> silent   Silent
import { ON_CONTRACT, daysTo, fmtDay } from './placements';
import { matchLead } from './sendLink';

export const ENDING_DAYS = 28;
export const REPLACEMENT_STATUS = { not_started: 'Not started', texted: 'Texted', texted_twice: 'Texted twice', next_requested: 'Next requested',
  extend_requested: 'Extend requested', leaving: 'Leaving', silent: 'Silent' };
const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const isTestCandidate = (c) => !!c && Array.isArray(c.tags) && c.tags.some((x) => s(x).toLowerCase() === 'test');
const national = (v) => { const d = s(v).replace(/\D/g, ''); return d.length === 11 && d.startsWith('1') ? d.slice(1) : d; };

/** The agent's steps for this placement at its current end date. -> [{ step, at }] oldest first */
export function replacementSteps(p, decisions = []) {
  const key = `${s(p.id)}@${s(p.end_date)}`;
  const out = [];
  for (const d of decisions || []) {
    if (s(d.decision) !== 'replacement') continue;
    const r = s(d.reason);
    const i = r.indexOf(':');
    if (i < 0 || r.slice(i + 1) !== key) continue;
    out.push({ step: r.slice(0, i), at: d.created_at });
  }
  return out.sort((a, b) => s(a.at).localeCompare(s(b.at)));
}

/** -> machine value (REPLACEMENT_STATUS key) */
export function replacementStatus(p, decisions = [], now = Date.now()) {
  const steps = replacementSteps(p, decisions);
  const has = (x) => steps.some((st) => st.step === x);
  if (!steps.length) return 'not_started';
  if (has('reply_leaving')) return 'leaving';
  if (has('reply_extend')) return 'extend_requested';
  if (has('ask_state') || has('reply_next')) return 'next_requested';
  if (has('nudge')) {
    const last = steps.filter((st) => st.step === 'nudge').pop();
    const at = Date.parse(last.at);
    return !Number.isNaN(at) && now - at > 7 * 864e5 ? 'silent' : 'texted_twice';
  }
  return has('opener') ? 'texted' : 'not_started';
}

/** The thread to open: the conversation on the candidate's number, else none (Text then opens the compose box by lead). */
const threadFor = (candidate, conversations = []) => {
  const p = national(candidate && candidate.phone);
  return p.length === 10 ? (conversations || []).find((c) => national(c.candidate_phone) === p) || null : null;
};

/**
 * -> [{ placement, candidate, days, status, statusText, lead, conversationId }] soonest end first.
 * Test candidates are left out; a placement whose candidate is not loaded is left out (nothing to show or text).
 */
export function endingSoon({ placements = [], candidates = [], decisions = [], leads = [], conversations = [], now = Date.now(), days = ENDING_DAYS } = {}) {
  const byId = new Map((candidates || []).map((c) => [String(c.id), c]));
  const out = [];
  for (const p of placements || []) {
    if (!ON_CONTRACT.has(s(p.status)) || !p.end_date) continue;
    const d = daysTo(p.end_date, now);
    if (d === null || d < 0 || d > days) continue;
    const candidate = byId.get(String(p.candidate_id));
    if (!candidate || isTestCandidate(candidate)) continue;
    const status = replacementStatus(p, decisions, now);
    const conv = threadFor(candidate, conversations);
    out.push({ placement: p, candidate, days: d, status, statusText: REPLACEMENT_STATUS[status], lead: matchLead(candidate, leads), conversationId: conv ? conv.id : null });
  }
  return out.sort((a, b) => s(a.placement.end_date).localeCompare(s(b.placement.end_date)));
}

/** "Synthetic Regional Hospital · ends Nov 19 (in 28 days)" */
export function endingLine(x) {
  const when = x.days === 0 ? 'today' : x.days === 1 ? 'tomorrow' : `in ${x.days} days`;
  return `${s(x.placement.facility) || 'A facility'} · ends ${fmtDay(x.placement.end_date)} (${when})`;
}

/** The candidate ids ending soon (the Candidates filter). */
export const endingSoonIds = (items) => new Set((items || []).map((x) => String(x.candidate.id)));
